import React from 'react';
import { Divider, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import type { AccommodationListingDTO, AccommodationsConsoleState, Filters, RenderListingEntryFn } from '@shared/types/Accommodations.types';
import { ListingEntry } from './components/ListingEntry';
import { CoupleRecommendationsCheck } from './components/CoupleRecommendationsCheck';
import { useAccommodationsListingMobileLayout } from '../../hooks/useAccommodationsListingMobileLayout';
import { SpecialEventRatesTag } from './components/SpecialEventRatesTag';
import DeskTopListingsHeader from './components/DeskTopListingsHeader';
import { pxToRem } from '@withjoy/joykit/theme';
import { ResetFiltersHeader } from './components/ResetFiltersHeader';
import type { SortOption, HotelSearchSource } from '@shared/constants/accommodations';
import { ErrorState } from '../ErrorState';

type ListingsPanelProps = Readonly<{
  hidePanel?: boolean;
  isLoadingPrices: boolean;
  listings: ReadonlyArray<AccommodationListingDTO>;
  state: AccommodationsConsoleState;
  sortOption: SortOption;
  onSortChange: (sort: SortOption) => void;
  noOfNights: number;
  filters: Filters;
  updateFilters: (filters: Filters) => void;
  hasError: boolean;
  sourceName: HotelSearchSource;
  includeCouplesPicks: boolean;
  renderListingEntry: RenderListingEntryFn;
  currencyCode: string;
  navigatorLanguage: string;
  dynamicPriceMax: number;
  showFundSavingsSort?: boolean;
  accentColor?: string;
  /** Desktop panel width; the map fills the rest. Undefined keeps the shared default. */
  listingsPanelWidth?: string;
  /**
   * Pinned paid-placement unit (the featured-placement A/B test), and how many organic results
   * render above it. Undefined on `control` and for every non-guest scenario, which keeps the
   * loop below byte-for-byte what ships today.
   */
  featuredPlacement?: Readonly<{ content: React.ReactNode; insertAfterIndex: number }>;
  /** The map area held no stays, so the list fell back to every result and says so. */
  isShowingOutsideViewport?: boolean;
}>;

/** The width every scenario has shipped with; a scenario opts out by passing its own. */
export const DEFAULT_PANEL_WIDTH = pxToRem(821);

const Container: React.FC<{ hide?: boolean; useNewMobileLayout?: boolean; panelWidth?: string }> = ({ hide, children, useNewMobileLayout, panelWidth }) => {
  return (
    <Flex
      height="fit-content"
      flexDirection="column"
      display={hide ? 'none' : 'flex'}
      width={{ _: 'full', md: panelWidth ?? DEFAULT_PANEL_WIDTH }}
      paddingLeft={useNewMobileLayout ? 4 : 6}
      paddingY={{ _: 6, sm2: 8 }}
      paddingRight={{ _: useNewMobileLayout ? 4 : 6, sm2: 8 }}
      rowGap={{ _: 6, sm2: 6 }}
    >
      {children}
    </Flex>
  );
};

const ListingsPanel = (props: ListingsPanelProps) => {
  const {
    hidePanel,
    listings,
    state,
    noOfNights,
    sortOption,
    onSortChange,
    filters,
    updateFilters,
    hasError,
    sourceName,
    includeCouplesPicks,
    renderListingEntry,
    currencyCode,
    navigatorLanguage,
    dynamicPriceMax,
    showFundSavingsSort,
    accentColor,
    listingsPanelWidth,
    featuredPlacement,
    isShowingOutsideViewport,
  } = props;
  const { isMobileOrTablet, shouldUseNewMobileLayout } = useAccommodationsListingMobileLayout();
  const { tq } = useTranslation('accommodationsConsole');

  // Clamped so a short page (filters down to one result) still shows the placement rather than
  // dropping it off the end.
  const featuredInsertIndex = featuredPlacement ? Math.min(featuredPlacement.insertAfterIndex, listings.length) : -1;

  return (
    <Container
      hide={hidePanel}
      useNewMobileLayout={shouldUseNewMobileLayout}
      panelWidth={listingsPanelWidth}
    >
      {isMobileOrTablet ? (
        <SpecialEventRatesTag />
      ) : (
        <DeskTopListingsHeader
          sortOption={sortOption}
          onSortChange={onSortChange}
          includeCouplesPicks={includeCouplesPicks}
          includeFundSavings={showFundSavingsSort}
          accentColor={accentColor}
        />
      )}
      {hasError ? (
        <ErrorState />
      ) : (
        <>
          <ResetFiltersHeader
            filters={filters}
            updateFilters={updateFilters}
            currencyCode={currencyCode}
            navigatorLanguage={navigatorLanguage}
            dynamicPriceMax={dynamicPriceMax}
          />
          {/* Ahead of the featured unit: it explains what the whole list below is, paid placement
              included, so it cannot sit underneath the thing it is describing. */}
          {isShowingOutsideViewport && (
            <TextV2
              color="mono12"
              typographyVariant="body1"
            >
              {tq`No stays in this map area — showing all nearby stays. Move or zoom out the map to focus on a different area.`}
            </TextV2>
          )}
          {featuredInsertIndex === 0 && featuredPlacement?.content}
          {listings.map((listing, index) => (
            <React.Fragment key={listing.id}>
              {renderListingEntry({ listing, state, noOfNights, sourceName })}
              {shouldUseNewMobileLayout && <Divider />}
              {featuredInsertIndex === index + 1 && featuredPlacement?.content}
            </React.Fragment>
          ))}
        </>
      )}
    </Container>
  );
};

// Takes the same width as the real panel. Without it the skeleton sits at the shared default and
// the whole column jumps sideways the moment listings arrive.
ListingsPanel.Skeleton = ({
  isMobileOrTablet,
  listingsPanelWidth,
  renderListingEntrySkeleton,
}: {
  isMobileOrTablet: boolean;
  listingsPanelWidth?: string;
  /** A scenario whose tile has its own shape passes its own card here; omitted keeps the shared one. */
  renderListingEntrySkeleton?: () => React.ReactNode;
}) => {
  const listingCard = renderListingEntrySkeleton?.() ?? <ListingEntry.Skeleton />;

  return (
    <Container panelWidth={listingsPanelWidth}>
      {isMobileOrTablet ? <SpecialEventRatesTag.Skeleton /> : <CoupleRecommendationsCheck.Skeleton />}
      {[0, 1, 2, 3].map(index => (
        <React.Fragment key={index}>
          {index > 0 && isMobileOrTablet && <Divider />}
          {listingCard}
        </React.Fragment>
      ))}
    </Container>
  );
};

export { ListingsPanel };
